import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Stack,
  Flex,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  IconButton,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaCopy } from 'react-icons/fa';
import { react_api } from '../../../../config';

const Explain = ({ 
  visualization, 
  selectedGoal, 
  username, 
  bearerToken, 
  themeColors 
}) => {
  // Local states for explanation
  const [explanations, setExplanations] = useState(null);
  const [isExplainLoading, setIsExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState(null);
  
  // Colors for sections
  const sectionColors = {
    accessibility: "teal",
    transformation: "purple",
    visualization: "blue",
    insight: "orange",
    code: "gray"
  };

  // Effect to automatically explain the visualization when the component is loaded or when dependencies change
  useEffect(() => {
    if (visualization && selectedGoal && visualization.length > 0 && visualization[0].code) {
      explainVisualization();
    }
  }, [visualization, selectedGoal]);

  // Function to request visualization explanation
  const explainVisualization = async () => {
    if (!visualization || !selectedGoal || !visualization[0].code) return;
    
    setIsExplainLoading(true);
    setExplainError(null);
    
    try {
      // Make API call to explain the visualization
      const response = await axios.post(`${react_api}/externalService/novaServices`, {
        username: username,
        BearerToken: bearerToken,
        service: 5,
        request: {
          code: visualization[0].code,
          goal: selectedGoal,          
          library: "seaborn"
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check the response format and save the explanations
      if (response.data && response.data.request && response.data.request.explanations) {
        setExplanations(response.data.request);
      } else {
        throw new Error('Invalid response format from explanation API');
      }
      
    } catch (err) {
      console.error('Error explaining visualization:', err);
      
      let errorMessage = 'Failed to get visualization explanation. Please try again.';
      
      if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        }
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setExplainError(errorMessage);
    } finally {
      setIsExplainLoading(false);
    }
  };

  // Function to copy code to clipboard
  const toast = useToast();
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: "Codice copiato!",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top"
        });
      },
      (err) => {
        toast({
          title: "Errore nella copia del codice",
          description: err.toString(),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top"
        });
      }
    );
  };

  // Render explanation section
  const renderExplanationSection = (section) => {
    const colorScheme = sectionColors[section.section] || "gray";
    
    return (
      <Box 
        key={section.section} 
        mb={6} 
        p={5} 
        borderWidth="1px" 
        borderRadius="md" 
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
      >
        <Flex align="center" mb={3}>
          <Badge colorScheme={colorScheme} fontSize="md" px={2} py={1} borderRadius="md">
            {section.section.charAt(0).toUpperCase() + section.section.slice(1)}
          </Badge>
        </Flex>
        
        <Text mb={4}>{section.explanation}</Text>
        
        {section.code && (
          <>
            <Divider my={3} />
            <Box>
              <Text fontWeight="medium" mb={2} fontSize="sm" color="gray.600">Code:</Text>
              <Box 
                position="relative"
                borderRadius="md"
              >
                <Tooltip label="Copia il codice" placement="top">
                  <IconButton
                    aria-label="Copy code"
                    icon={<FaCopy />}
                    size="sm"
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={2}
                    onClick={() => copyToClipboard(section.code)}
                    bg="whiteAlpha.800"
                    _hover={{ bg: "whiteAlpha.900" }}
                  />
                </Tooltip>
                <style jsx global>{`
                  .hide-scrollbars code {
                    scrollbar-width: none !important;
                    -ms-overflow-style: none !important;
                  }
                  .hide-scrollbars code::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                  }
                `}</style>
                <SyntaxHighlighter
                  language="python"
                  style={atomDark}
                  customStyle={{
                    margin: 0,
                    padding: '16px',
                    paddingTop: '40px',
                    borderRadius: '0.375rem',
                    fontSize: '0.8rem',
                    backgroundColor: '#1e1e1e',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    overflow: 'auto'
                  }}
                  showLineNumbers={true}
                  wrapLines={true}
                  wrapLongLines={true}
                >
                  {section.code}
                </SyntaxHighlighter>
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Show loading spinner during explanation loading */}
      {isExplainLoading && (
        <Box textAlign="center" p={4}>
          <Spinner size="lg" color={themeColors?.titleIconColor || '#9acd32'} />
          <Text mt={2} color={themeColors?.fontColor || '#0b4e5aff'}>
            Analyzing and explaining your visualization...
          </Text>
        </Box>
      )}

      {/* Show error message in case of problems */}
      {explainError && (
        <Box mt={4}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>{explainError}</Text>
          </Alert>
        </Box>
      )}

      {/* Show explanations when available */}
      {explanations && !isExplainLoading && (
        <Box mt={4}>
          <Heading as="h4" size="md" mb={4} color={themeColors?.fontColor || '#0b4e5aff'}>
            Visualization Explanation
          </Heading>
          
          <Text fontSize="sm" color="gray.600" mb={6}>
            A detailed breakdown of your visualization's components, transformations, and design choices.
          </Text>
          
          <Stack spacing={4}>
            {explanations.explanations.map(section => renderExplanationSection(section))}
          </Stack>
        </Box>
      )}

      {/* Show initial message when explanations are not available */}
      {!explanations && !isExplainLoading && !explainError && (
        <Box
          borderWidth="1px"
          borderRadius="md"
          p={4}
          bg={themeColors?.servicesBg || '#f6f3ec'}
          borderColor={themeColors?.borderColor || '#178da1ff'}
          boxShadow="sm"
        >
          <Text fontSize="md" color={themeColors?.fontColor || '#0b4e5aff'}>
            Getting explanations for your data visualization...
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Explain;
